/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import {
  Box,
  Flex,
  Text,
  TextField,
  Checkbox,
  IconButton,
  Button,
  Spinner,
} from "@radix-ui/themes";
import {
  MagnifyingGlassIcon,
  Cross2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@radix-ui/react-icons";
import { useManagerContext } from "./context";

/**
 * DynamicSearch - A search component that shows only values present in the current dataset.
 *
 * Like Excel's column filters, it fetches the distinct values from the data
 * and allows multi-select filtering with search.
 */

type DynamicOption = {
  value: string | number;
  label: string;
};

type FetchDistinctFn = (
  filterKey: string,
  otherFilters?: Record<string, unknown>,
) => Promise<DynamicOption[]>;

type DynamicSearchContextValue = {
  fetchDistinctValues: FetchDistinctFn;
  // Track which filters are active so we can exclude current filter when fetching options
  activeFilters: Record<string, unknown>;
};

const DynamicSearchContext = createContext<DynamicSearchContextValue | null>(
  null,
);

function useDynamicSearchContext() {
  const context = useContext(DynamicSearchContext);
  if (!context) {
    throw new Error(
      "DynamicSearch components must be used within DynamicSearch.Root",
    );
  }
  return context;
}

// ============================================================================
// Root Component
// ============================================================================

type DynamicSearchRootProps = {
  children: ReactNode;
  /**
   * Function to fetch distinct values for a given filter key.
   * Should return an array of { value, label } options.
   *
   * @param filterKey - The key of the filter (e.g., "product_id")
   * @param otherFilters - Other active filters to consider when fetching options
   */
  fetchDistinctValues: FetchDistinctFn;
};

function DynamicSearchRoot({
  children,
  fetchDistinctValues,
}: DynamicSearchRootProps) {
  const { searchParams } = useManagerContext();

  return (
    <DynamicSearchContext.Provider
      value={{
        fetchDistinctValues,
        activeFilters: searchParams.filters ?? {},
      }}
    >
      {children}
    </DynamicSearchContext.Provider>
  );
}

// ============================================================================
// Filter Component
// ============================================================================

type DynamicSearchFilterProps = {
  filterKey: string;
  label: string;
  defaultExpanded?: boolean;
};

function DynamicSearchFilter({
  filterKey,
  label,
  defaultExpanded = false,
}: DynamicSearchFilterProps) {
  const { fetchDistinctValues, activeFilters } = useDynamicSearchContext();
  const { searchParams, setFilter } = useManagerContext();

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [options, setOptions] = useState<DynamicOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const fetchIdRef = useRef(0);

  // Get current selected values for this filter
  const currentValues =
    (searchParams.filters?.[filterKey] as (string | number)[] | undefined) ??
    [];

  // Fetch options only once when first expanded
  const loadOptions = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    try {
      // Pass other active filters (excluding current filter)
      // Capture current filters at the time of fetch
      const otherFilters = { ...activeFilters };
      delete otherFilters[filterKey];

      const result = await fetchDistinctValues(filterKey, otherFilters);

      // Only update if this is still the latest request
      if (fetchId === fetchIdRef.current) {
        setOptions(result);
      }
    } catch (err) {
      if (fetchId === fetchIdRef.current) {
        console.error("Failed to fetch distinct values:", err);
        setError("Failed to load options");
      }
    } finally {
      if (fetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [fetchDistinctValues, filterKey, activeFilters]);

  // Compute other filters (excluding this filter) to detect cross-filter changes
  const otherFiltersKey = Object.entries(activeFilters)
    .filter(([key]) => key !== filterKey)
    .map(([key, val]) => `${key}:${JSON.stringify(val)}`)
    .sort()
    .join("|");

  // Fetch options when first expanded, and refetch when other filters change
  useEffect(() => {
    if (expanded) {
      loadOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, otherFiltersKey]);

  // Filter options by search text
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchText.toLowerCase()),
  );

  // Handlers
  const handleToggle = (value: string | number) => {
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    setFilter(filterKey, newValues.length > 0 ? newValues : undefined);
  };

  const handleSelectAll = () => {
    const allValues = filteredOptions.map((opt) => opt.value);
    // Merge with existing selections (in case some are filtered out)
    const newValues = [...new Set([...currentValues, ...allValues])];
    setFilter(filterKey, newValues.length > 0 ? newValues : undefined);
  };

  const handleClearAll = () => {
    setFilter(filterKey, undefined);
  };

  const selectedCount = currentValues.length;
  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => currentValues.includes(opt.value));

  return (
    <Box>
      {/* Header - click to expand/collapse */}
      <Flex
        justify="between"
        align="center"
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <Text as="div" size="2" weight="medium">
          {label}
          {selectedCount > 0 && (
            <Text size="1" color="gray" ml="1">
              ({selectedCount})
            </Text>
          )}
        </Text>
        <IconButton
          variant="ghost"
          size="1"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </IconButton>
      </Flex>

      {/* Expanded content */}
      {expanded && (
        <Box mt="2">
          {/* Search input */}
          <TextField.Root
            size="1"
            placeholder="Filter..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            mb="2"
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height="14" width="14" />
            </TextField.Slot>
            {searchText && (
              <TextField.Slot>
                <IconButton
                  size="1"
                  variant="ghost"
                  onClick={() => setSearchText("")}
                >
                  <Cross2Icon height="12" width="12" />
                </IconButton>
              </TextField.Slot>
            )}
          </TextField.Root>

          {/* Select All / Clear All buttons */}
          <Flex gap="2" mb="2">
            <Button
              size="1"
              variant="soft"
              onClick={handleSelectAll}
              disabled={loading || filteredOptions.length === 0}
            >
              Select All
            </Button>
            <Button
              size="1"
              variant="soft"
              onClick={handleClearAll}
              disabled={selectedCount === 0}
            >
              Clear
            </Button>
          </Flex>

          {/* Loading state */}
          {loading && (
            <Flex justify="center" py="3">
              <Spinner size="2" />
            </Flex>
          )}

          {/* Error state */}
          {error && (
            <Text size="1" color="red">
              {error}
            </Text>
          )}

          {/* Options list */}
          {!loading && !error && (
            <Flex
              direction="column"
              gap="1"
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              {filteredOptions.length === 0 ? (
                <Text size="1" color="gray">
                  {options.length === 0
                    ? "No options available"
                    : "No matches found"}
                </Text>
              ) : (
                <>
                  {/* "All" checkbox for filtered options */}
                  {filteredOptions.length > 1 && (
                    <Text
                      as="label"
                      size="2"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        fontStyle: "italic",
                        color: "var(--gray-11)",
                      }}
                    >
                      <Checkbox
                        size="1"
                        checked={allFilteredSelected}
                        onCheckedChange={() => {
                          if (allFilteredSelected) {
                            // Deselect all filtered options
                            const filteredValues = new Set(
                              filteredOptions.map((opt) => opt.value),
                            );
                            const newValues = currentValues.filter(
                              (v) => !filteredValues.has(v),
                            );
                            setFilter(
                              filterKey,
                              newValues.length > 0 ? newValues : undefined,
                            );
                          } else {
                            handleSelectAll();
                          }
                        }}
                      />
                      (Select all {filteredOptions.length})
                    </Text>
                  )}
                  {filteredOptions.map((option) => {
                    const isSelected = currentValues.includes(option.value);
                    return (
                      <Text
                        key={String(option.value)}
                        as="label"
                        size="2"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                        }}
                      >
                        <Checkbox
                          size="1"
                          checked={isSelected}
                          onCheckedChange={() => handleToggle(option.value)}
                        />
                        {option.label}
                      </Text>
                    );
                  })}
                </>
              )}
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Export
// ============================================================================

export const DynamicSearch = {
  Root: DynamicSearchRoot,
  Filter: DynamicSearchFilter,
};
